import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Paper, FormControlLabel, Checkbox, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { FieldValues, useFieldArray, useForm, Control } from "react-hook-form";
import { toast } from "react-toastify";
import agent from "../../../app/api/agent";
import AutocompleteInputOption from "../../../app/models/autocompleteInputOption";
import { Order, PaymentOrderState } from "../../../app/models/order";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import { setAdminOrder } from "./adminOrdersSlice";
import { adminOrderValidationSchema } from "./adminOrderValidation";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import AppReturn from "../../../app/components/AppReturn";
import dayjs from "dayjs";
import { orderFormCalculateSum, states } from "../../../app/util/util";
import { OrderFormValues } from "../../../app/models/orderFormValues";
import OrderFormBasicInformation from "../../../app/components/Order/OrderFormBasicInformation";
import OrderFormPayerData from "../../../app/components/Order/OrderFormPayerData";
import OrderFormBenefitients from "../../../app/components/Order/OrderFormBenefitients";
import OrderValueBox from "../../../app/components/Order/OrderValueBox";
import OrderFormEducationEffectsModal from "../../../app/components/Order/OrderFormEducationEffectsModal";
import { setClientOrder } from "../../client/orders/clientOrdersSlice";
import { useServicePriceFetching } from "../../../app/hooks/useServicePriceFetching";
import DraftRestoreButton from "../../../app/components/Draft/DraftRestoreButton";
import { useEnhancedFormDraft } from "../../../app/hooks/useEnhancedFormDraft";
import { useEducationEffects } from "../../../app/hooks/useEducationEffects";
import AppUnsavedChangesDialog from "../../../app/components/AppUnsavedChangesDialog";
import { useBlockNavigation } from "../../../app/hooks/useBlockNavigation";
import DraftRestoreDialog from "../../../app/components/Draft/DraftRestoreDialog";

interface Props {
    order?: Order;
    cancelEdit: () => void;
}

export default function AdminOrderForm({ order, cancelEdit }: Props) {
    const { control, reset, handleSubmit, setValue, watch, getValues,
        formState: { errors, isDirty, isSubmitting } } = useForm<OrderFormValues>({
            resolver: yupResolver(adminOrderValidationSchema),
            defaultValues: {
                serviceId: 0,
                customeClientServiceName: '',
                preposedDateTime: '',
                placeOfServiceAndAnotherInformations: '',
                payerInstitutionName: '',
                payerLastName: '',
                payerFirstName: '',
                payerStreet: '',
                payerBuilding: '',
                payerZip: '',
                payerCity: '',
                payerState: '',
                payerCountry: '',
                payerNIP: '',
                rows: [],
                paymentOrderState: PaymentOrderState.PENDING,
                generateAITest: true // Default to checked for SuperAdmin
            }
        });

    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.account.user);
    const isSuperAdmin = !!(user && user.roles?.includes('SuperAdmin'));
    const [possibleServices, setPossibleServices] = useState<AutocompleteInputOption[]>([]);
    const serviceId = watch('serviceId');
    const [manualServiceChange, setManualServiceChange] = useState(false);
    const [unitDiscountedPrice, setUnitDiscountedPrice] = useState<number>(0);
    const [sum, setSum] = useState<number>(0);
    const [totalItemRows, setTotalItemRows] = useState(0);
    const [generateAITest, setGenerateAITest] = useState(true); // Checkbox state

    // State for unsaved changes dialog
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    // State to store proceed and cancel functions from the blocker
    const [proceedNavigationFn, setProceedNavigationFn] = useState<() => void>(() => { });
    const [cancelNavigationFn, setCancelNavigationFn] = useState<() => void>(() => { });
    // State for manual navigation (cancel button)
    const [pendingManualNavigation, setPendingManualNavigation] = useState<(() => void) | null>(null);

    // Draft restore dialog state
    const [showDraftRestoreDialog, setShowDraftRestoreDialog] = useState(false);

    // Helper to check if education effects have actual content
    const hasValidEducationEffects = (effects: string | undefined): boolean => {
        if (!effects) return false;
        const cleaned = effects.trim().replace(/;+/g, '').trim();
        return cleaned.length > 0;
    };

    // Helper to check if order has valid questions JSON
    const hasValidQuestionsJson = (questionsJson: string | undefined): boolean => {
        if (!questionsJson) return false;
        try {
            const parsed = JSON.parse(questionsJson);
            if (Array.isArray(parsed)) return parsed.length > 0;
            if (typeof parsed === 'object') return Object.keys(parsed).length > 0;
            return false;
        } catch {
            return false;
        }
    };

    // Watch education effects for checkbox state
    const watchedEducationEffects = watch('educationEffects');
    const orderHasQuestions = order && hasValidQuestionsJson(order.questionsJson);
    const canGenerateAI = hasValidEducationEffects(watchedEducationEffects) && !orderHasQuestions;

    // Determine checkbox disabled state and tooltip
    const getAICheckboxState = () => {
        if (orderHasQuestions) {
            return { disabled: true, tooltip: 'Pytania zostały już wygenerowane' };
        }
        if (!hasValidEducationEffects(watchedEducationEffects)) {
            return { disabled: true, tooltip: 'Uzupełnij efekty kształcenia' };
        }
        return { disabled: false, tooltip: '' };
    };

    const aiCheckboxState = getAICheckboxState();

    const handleSetTotalItemRows = (count: number) => {
        setTotalItemRows(count);
        setSum(orderFormCalculateSum(count, unitDiscountedPrice));
    }

    const formType = 'adminOrder';
    const formId = 'new'; // Use 'new' for new forms, disable draft for existing orders

    const fieldsToExcludeFromDraft = [
        'paymentOrderState'
    ];

    // Use our enhanced form draft hook, only for new forms
    const {
        storedDraft,
        isRestoringDraft,
        draftRestorationRef,
        shouldFetchPrice,
        restoredServiceId,
        setShouldFetchPrice,
        setRestoredServiceId,
        handleRestoreDraft,
        clearDraft,
        getDraftSummary,
        draftShownOnLoad,
        draftDeleteCountdown,
        resetCountdown,
        getDraft,
        setDraftDeleteCountdown
    } = useEnhancedFormDraft<OrderFormValues>(
        // Only use draft when creating a new form, not editing
        order ? '' : formType,
        order ? '' : formId,
        { reset, setValue, getValues, isDirty },
        {
            excludeFields: fieldsToExcludeFromDraft,
        }
    );

    // Get draft summary for dialog
    const draftSummary = storedDraft ? getDraftSummary(storedDraft) : { draftDate: '', fields: [] };

    // Check for drafts on component mount
    useEffect(() => {
        // Just make sure we've checked for drafts
        if (!storedDraft && getDraft && !order) {
            const existingDraft = getDraft();
            if (existingDraft) {
                console.log('Found draft on component mount');
                // The useEnhancedFormDraft hook will handle state updating
            }
        }
    }, [getDraft, storedDraft, order]);

    // Show draft restore dialog when a draft is found on load
    useEffect(() => {
        if (!order && storedDraft && draftShownOnLoad) {
            setShowDraftRestoreDialog(true);
        }
    }, [storedDraft, draftShownOnLoad, order]);

    // Use route blocker to prevent leaving with unsaved changes
    useBlockNavigation(
        isDirty,
        (proceed, cancel) => {
            // Store the navigation functions
            setProceedNavigationFn(() => proceed);
            setCancelNavigationFn(() => cancel);
            // Show the confirmation dialog
            setShowUnsavedChangesDialog(true);
        }
    );

    // Use admin-specific education effects hook
    const {
        educationEffects,
        setEducationEffects,
        openModal,
        handleOpenModal,
        handleCloseModal,
        addNewEffect,
        updateEffect,
        removeEffect,
        handleSaveEducationEffects
    } = useEducationEffects({
        serviceId,
        manualServiceChange,
        setValue,
        watch,
        isRestoringDraft,
        draftRestorationRef,
        role: 'admin'
    });

    // Use service price fetching hook
    useServicePriceFetching({
        serviceId,
        isRestoringDraft,
        shouldFetchPrice,
        restoredServiceId,
        order,
        setShouldFetchPrice,
        setRestoredServiceId,
        setUnitDiscountedPrice,
        draftRestorationRef,
        userRole: 'admin'  // Specify admin role
    });

    // Set up field array for beneficiaries
    const { fields, append, remove } = useFieldArray({
        control,
        name: "rows"
    });

    // Function to handle cancel with unsaved changes check
    const handleCancel = useCallback(() => {
        if (isDirty) {
            // For manual navigation (buttons), we use a different approach
            setShowUnsavedChangesDialog(true);
            setPendingManualNavigation(() => {
                return () => {
                    // Don't clear drafts - this preserves the draft when navigating away
                    cancelEdit();
                };
            });
        } else {
            // If form is not dirty, we can navigate away directly
            // Also don't clear drafts here
            cancelEdit();
        }
    }, [isDirty, cancelEdit]);

    const handleConfirmNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // Handle navigation based on source:
        // 1. If it's a router-based navigation
        if (proceedNavigationFn) {
            proceedNavigationFn();
        }

        // 2. If it's a manual navigation (from cancel button)
        if (pendingManualNavigation) {
            pendingManualNavigation();
            setPendingManualNavigation(null);
        }
    }, [proceedNavigationFn, pendingManualNavigation]);

    // Function to cancel navigation away (stay on page)
    const handleCancelNavigation = useCallback(() => {
        setShowUnsavedChangesDialog(false);

        // If it's a router-based navigation
        if (cancelNavigationFn) {
            cancelNavigationFn();
        }

        // For manual navigation, just close the dialog
        setPendingManualNavigation(null);
    }, [cancelNavigationFn]);

    // Prevent accidental window close or navigation away
    useEffect(() => {
        // Only add the event listener if the form has unsaved changes
        if (isDirty) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
                return '';
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [isDirty]);

    // Load possible services
    useEffect(() => {
        agent.Services.fetchPossibleServices()
            .then(result => setPossibleServices(result))
            .catch(error => console.log(error));
    }, []);

    // Load existing order data if editing
    useEffect(() => {
        if (order && !isDirty) {
            const rows = (order?.benefitients ?? '').split(';').map(item => {
                const [firstName, lastName, pesel, birthPlace, birthDate, certId] = item.split('~');
                return { firstName, lastName, pesel, birthPlace, birthDate, certId };
            });
            reset({ ...order, rows, preposedDateTime: dayjs(order.preposedDateTime).toISOString() });
            setValue('educationEffects', order.educationEffects, { shouldValidate: true });
            setEducationEffects(order.educationEffects?.split(';') || []);
        }
    }, [order, reset, isDirty, setValue, setEducationEffects]);

    // Show validation errors
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).map(error => error?.message ?? '').join(', ');
            toast.error(`Sprawdź formularz: ${errorMessages}`);
        }
    }, [errors]);

    // Handle form submission with Admin-specific logic
    async function handleSubmitData(data: OrderFormValues) {
        if (!data.serviceId) {
            toast.error('Wybierz egzamin przed zapisaniem formularza');
            return;
        }

        try {
            // Calculate benefitients with potential certId for admin functionality
            data.benefitients = data.rows.map(row =>
                `${row.firstName}~${row.lastName}~${row.pesel}~${row.birthPlace}~${row.birthDate}${row.certId == null || row.certId == undefined ? '' : '~' + row.certId
                }`
            ).join(';');

            // Always set totalItem based on actual rows count
            data.totalItem = data.rows.length;

            // Include generateAITest flag for SuperAdmin
            if (isSuperAdmin && !order) {
                data.generateAITest = generateAITest && canGenerateAI;
            } else {
                data.generateAITest = false;
            }

            let response: Order;
            if (order) {
                // Updating existing order
                response = await agent.Admin.Orders.update(data);
                toast.success('Uaktualniono zamówienie');

                // Auto-regeneration disabled - SuperAdmin must manually regenerate via AdminOrderDetails

                // Fetch updated data only for existing orders
                const updatedOrder = await agent.Admin.Orders.details(response.publicId);
                if (updatedOrder) {
                    reset({
                        ...updatedOrder,
                        rows: updatedOrder.benefitients.split(';').map((item: string) => {
                            const [firstName, lastName, pesel, birthPlace, birthDate, certId] = item.split('~');
                            return { firstName, lastName, pesel, birthPlace, birthDate, certId };
                        })
                    });
                    setValue('educationEffects', updatedOrder.educationEffects, { shouldValidate: true });
                    setEducationEffects(updatedOrder.educationEffects?.split(';') || []);
                }
            } else {
                // Creating new order
                response = await agent.Admin.Orders.create(data);
                toast.success('Utworzono zamówienie');

                // Show AI generation toast only for SuperAdmin when checkbox was checked
                if (isSuperAdmin && data.generateAITest) {
                    toast.info('Pytania testowe zostaną wygenerowane w tle.', {
                        autoClose: 5000
                    });
                }

                // Clear draft when creating new order successfully
                clearDraft();
                setDraftDeleteCountdown(0);
            }

            // Admin-specific: update both admin and client stores
            dispatch(setAdminOrder(response));
            dispatch(setClientOrder(response));

            // Close form
            cancelEdit();

        } catch (error) {
            console.log(error);
            toast.error('Wystąpił błąd podczas zapisywania zamówienia');
        }
    }

    // Fill with test data (admin only)
    const fillWithTestData = () => {
        const testData: OrderFormValues = {
            serviceId: 1,
            serviceName: 'Testowy serviceName',
            customeClientServiceName: 'Testowy Egzamin',
            preposedDateTime: dayjs().toISOString(),
            placeOfServiceAndAnotherInformations: 'Testowe miejsce',
            clientComment: 'Testowy komentarz',
            trainingProgram: 'Testowy program szkoleniowy',
            educationEffects: 'Efekt1;Efekt2;Efekt3',
            payerClientName: 'Testowa Instytucja',
            payerClientAddress: 'Adres',
            payerClientNIP: 'NIP',
            payerInstitutionName: 'Testowa Instytucja',
            payerLastName: 'Kowalski',
            payerFirstName: 'Jan',
            payerStreet: 'Testowa 1',
            payerBuilding: '1',
            payerPremises: '2',
            payerZip: '00-001',
            payerCity: 'Warszawa',
            payerState: states[0],
            payerCountry: 'Polska',
            payerNIP: '1234567890',
            rows: [
                {
                    firstName: 'Jan',
                    lastName: 'Kowalski',
                    pesel: '12345678901',
                    birthPlace: 'Warszawa',
                    birthDate: '01.01.1990'
                },
                {
                    firstName: 'Anna',
                    lastName: 'Nowak',
                    pesel: '09876543210',
                    birthPlace: 'Kraków',
                    birthDate: '02.02.1985'
                }
            ],
            totalItem: 2,
            paymentOrderState: PaymentOrderState.PENDING
        };
        reset(testData);
        setEducationEffects(testData.educationEffects?.split(';') || []);
    };

    // Handlers for draft restore dialog
    const handleRestoreDialogClose = () => {
        setShowDraftRestoreDialog(false);
        // Reset the countdown timer when the dialog is closed without restoring
        if (storedDraft) {
            resetCountdown(); // Start 60-second countdown to delete the draft
        }
    };

    const handleRestoreDraftFromDialog = () => {
        if (storedDraft) {
            handleRestoreDraft(storedDraft);
            setShowDraftRestoreDialog(false);
        }
    };

    const handleDeleteDraft = () => {
        clearDraft();
        setShowDraftRestoreDialog(false);
    };

    const handleDraftRestoreButtonClick = () => {
        setShowDraftRestoreDialog(true);
        // Reset countdown when user clicks the button to re-open dialog
        resetCountdown();
    };

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Tworzenie/edycja zamówienia"} cancelEdit={handleCancel} />
            <Box component={Paper} sx={{ p: 4 }}>
                {/* Show the restore button floating on the side if there's a draft and we're not editing */}
                {!order && storedDraft && draftDeleteCountdown > 0 && (
                    <DraftRestoreButton
                        formType={formType}
                        formId={formId}
                        draft={storedDraft}
                        onRestore={handleDraftRestoreButtonClick}
                        countdown={draftDeleteCountdown}
                    />
                )}

                <form onSubmit={handleSubmit(handleSubmitData)}>
                    <Box display='flex' justifyContent='center'>
                        <Button onClick={fillWithTestData} variant='outlined' color='secondary'>Wypełnij Danymi</Button>
                    </Box>

                    <OrderFormBasicInformation
                        order={order}
                        control={control}
                        possibleServices={possibleServices}
                        watch={watch}
                        setValue={setValue}
                        handleOpenModal={handleOpenModal}
                        setManualServiceChange={() => setManualServiceChange(true)}
                        isAdmin={true}
                    />

                    <OrderFormPayerData control={control} setValue={setValue} />
                    <OrderFormBenefitients
                        control={control as unknown as Control<FieldValues>}
                        fields={fields}
                        append={append as unknown as (value: FieldValues | FieldValues[]) => void}
                        remove={remove}
                        handleSetTotalItemRows={handleSetTotalItemRows}
                        setValue={setValue}
                        watch={watch}
                    />
                    <OrderValueBox sum={sum} unitDiscountedPrice={unitDiscountedPrice} totalItem={totalItemRows} />

                    {/* AI Test Generation Checkbox - SuperAdmin only */}
                    {isSuperAdmin && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Generowanie testu AI
                            </Typography>
                            <Tooltip title={aiCheckboxState.tooltip} arrow placement="right">
                                <span>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={generateAITest && canGenerateAI}
                                                onChange={(e) => setGenerateAITest(e.target.checked)}
                                                disabled={aiCheckboxState.disabled}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography 
                                                variant="body2" 
                                                color={aiCheckboxState.disabled ? 'text.disabled' : 'text.primary'}
                                            >
                                                Generuj test AI
                                            </Typography>
                                        }
                                    />
                                </span>
                            </Tooltip>
                            {orderHasQuestions && (
                                <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                                    Pytania można ponownie wygenerować z poziomu szczegółów zamówienia.
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box display='flex' justifyContent='space-between' sx={{ mt: 3 }}>
                        <Button onClick={handleCancel} variant='outlined' color='primary'>Anuluj</Button>
                        <LoadingButton loading={isSubmitting} disabled={!isDirty} type='submit' variant='outlined' color='success'>Zapisz</LoadingButton>
                    </Box>

                    <OrderFormEducationEffectsModal
                        openModal={openModal}
                        handleCloseModal={handleCloseModal}
                        educationEffects={educationEffects}
                        updateEffect={updateEffect}
                        removeEffect={removeEffect}
                        addNewEffect={addNewEffect}
                        handleSaveEducationEffects={handleSaveEducationEffects}
                    />
                </form>
            </Box>

            {/* Regular unsaved changes dialog */}
            <AppUnsavedChangesDialog
                open={showUnsavedChangesDialog}
                onClose={handleCancelNavigation}
                onConfirm={handleConfirmNavigation}
            />

            {/* Draft restore dialog */}
            <DraftRestoreDialog
                open={showDraftRestoreDialog}
                onClose={handleRestoreDialogClose}
                onRestore={handleRestoreDraftFromDialog}
                onDelete={handleDeleteDraft}
                draftSummary={draftSummary}
            />
        </>
    );
}